import { localizedAccountDisplayName, uniqueDisplayAccount } from "../utils/localizedAccountDisplayName";
import type { ParseKeys } from "i18next";
import { useTranslation } from "react-i18next";
// src/pages/ExpenseList.tsx
import React, { useState, useEffect, useMemo } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { PaperClipIcon } from "@heroicons/react/24/outline";
import EmptyState from "../components/EmptyState";
import { apiGet } from "../api/client";
import { companyRoute } from "../utils/routeHelpers";
import { StatusBubble } from "../components/StatusBubble";
import { ListToolbar, MenuItem, SortOption } from "../components/ListToolbar";
import ListTableShell from "../components/lists/ListTableShell";
import { formatMoney } from "../utils/formatNumber";
import { A } from "../styles/appleClasses";
import { formatAnyDateToLocale } from "../utils/dateUtils";
import { Button } from "../components/Button";

interface Account {
  kind?: string | null;
  id: number;
  name: string;
  type: string;
}
interface Vendor {
  id: number;
  name: string;
}
interface ExpenseSourceDocument {
  id: number;
  original_filename?: string | null;
  content_type?: string | null;
  byte_size?: number | null;
  created_at: string;
}

// A real Expense row, returned by GET /expenses with record_type="expense".
// Identical to the pre-merge `Expense` shape in every field -- record_type
// is purely an additive discriminator (see backend/schemas.py's
// ExpenseListRow).
interface ExpenseRow {
  record_type: "expense";
  id: number;
  date: string;
  account_id: number;
  amount: number;
  description?: string;

  vendor?: Vendor | null;
  vendor_name?: string;

  status?: string;
  voided?: boolean;
  source_document?: ExpenseSourceDocument | null;
  lines?: { id: number; detail: string; amount: number }[];
}

// A Deposit write-off event, returned by GET /expenses with
// record_type="deposit_writeoff" -- see backend/schemas.py's
// DepositWriteoffListRow. Read-only: never an Expense, never a second
// journal. `id` is ALREADY a namespaced string ("deposit_writeoff:<n>"),
// distinct from ExpenseRow.id's bare number -- never re-derive or guess
// this shape from null fields.
interface DepositWriteoffRow {
  record_type: "deposit_writeoff";
  id: string;
  deposit_event_id: number;
  deposit_id: number;
  date: string;
  vendor_id?: number | null;
  vendor_name?: string | null;
  expense_account_id: number;
  expense_account_name?: string | null;
  amount: number;
  memo?: string | null;
  status: "Voided" | null;
  voided: boolean;
  journal_entry_id: number;
}

// A Prepaid recognition event, returned by GET /expenses with
// record_type="prepaid_expense" -- see backend/schemas.py's
// PrepaidExpenseListRow. Read-only: never an Expense, never a second
// journal. `id` is ALREADY a namespaced string ("prepaid_expense:<n>"),
// distinct from both ExpenseRow.id's bare number and
// DepositWriteoffRow.id's "deposit_writeoff:<n>" -- never re-derive or
// guess this shape from null fields. Only kind="expense" recognitions are
// projected (refund/writeoff are excluded server-side).
interface PrepaidExpenseRow {
  record_type: "prepaid_expense";
  id: string;
  prepaid_event_id: number;
  prepaid_id: number;
  date: string;
  vendor_id?: number | null;
  vendor_name?: string | null;
  expense_account_id: number;
  expense_account_name?: string | null;
  amount: number;
  memo?: string | null;
  status: "Voided" | null;
  voided: boolean;
  journal_entry_id: number;
}

// The merged GET /expenses row shape. `record_type` is the ONLY field any
// accessor below may branch on -- never infer row kind from which fields
// happen to be present/null, since ExpenseRow's own fields (vendor,
// status, source_document, ...) are already nullable for unrelated
// (on-account / pay-now) reasons.
type OperationalRow = ExpenseRow | DepositWriteoffRow | PrepaidExpenseRow;

function isWriteoffRow(row: OperationalRow): row is DepositWriteoffRow {
  return row.record_type === "deposit_writeoff";
}

function isPrepaidExpenseRow(row: OperationalRow): row is PrepaidExpenseRow {
  return row.record_type === "prepaid_expense";
}

// Both synthetic projections (deposit write-off and prepaid recognition)
// share the same read shape for the fields the list renders -- a namespaced
// string `id`, `expense_account_id`/`expense_account_name`, `vendor_name`,
// `amount`, and a Voided-only `status`/`voided` -- and neither is backed by
// a real Expense row. Adapters that only touch those shared fields branch on
// this combined guard; the two kinds are told apart individually only where
// they genuinely differ (drill-through target and sort tie-break id).
function isSyntheticRow(
  row: OperationalRow,
): row is DepositWriteoffRow | PrepaidExpenseRow {
  return row.record_type !== "expense";
}

// ---------------------------------------------------------------------------
// record_type-aware row adapters
//
// Every place in this file that reads a row's date, amount, vendor,
// category/account, status, id, or detail-navigation target MUST go
// through one of these -- never access `row.account_id`/`row.status`/etc.
// directly on a row of unknown type, since ExpenseRow and
// DepositWriteoffRow use different field names for the "same" concept
// (account_id vs expense_account_id) and different status vocabularies
// (Paid/Partial/Unpaid/Voided vs Voided-only).
// ---------------------------------------------------------------------------

function getVendorName(row: OperationalRow): string {
  if (isSyntheticRow(row)) return row.vendor_name || "";
  return row.vendor?.name || row.vendor_name || "";
}

function getCategoryName(row: OperationalRow, accounts: Account[]): string {
  if (isSyntheticRow(row)) {
    return (
      row.expense_account_name ||
      accounts.find((a) => a.id === row.expense_account_id)?.name ||
      "—"
    );
  }
  return accounts.find((a) => a.id === row.account_id)?.name || "—";
}

function rowDate(row: OperationalRow): string {
  return row.date;
}

function rowAmount(row: OperationalRow): number {
  return row.amount;
}

// Stable, always-string, always type-namespaced React key -- guarantees
// uniqueness even when an Expense id and a DepositEvent id are numerically
// equal, since ExpenseRow's key is always `expense-<n>` and
// DepositWriteoffRow's own `id` field is already `deposit_writeoff:<n>`
// (two different string shapes that can never collide).
function rowKey(row: OperationalRow): string {
  return isSyntheticRow(row) ? row.id : `expense-${row.id}`;
}

// Detail-click destination. A synthetic row NEVER navigates to Expense
// Detail -- there is no Expense record backing it. A write-off routes to
// its parent Deposit and a prepaid recognition routes to its parent
// Prepaid, each of which already lists this exact event in its own History
// section.
function rowDetailPath(row: OperationalRow, company_id?: string): string {
  if (isWriteoffRow(row)) {
    return companyRoute(company_id, `/deposits/${row.deposit_id}`);
  }
  if (isPrepaidExpenseRow(row)) {
    return companyRoute(company_id, `/prepaids/${row.prepaid_id}`);
  }
  return companyRoute(company_id, `/expenses/${row.id}`);
}

// Accessible label for a row's clickable card/row -- points at the drill-
// through destination (parent Deposit / parent Prepaid / the Expense
// itself), matching rowDetailPath.
function rowAriaLabel(row: OperationalRow, t: (key: ParseKeys, values: { id: string | number }) => string): string {
  if (isWriteoffRow(row)) return t("expenses.list.viewDeposit", { id: row.deposit_id });
  if (isPrepaidExpenseRow(row)) return t("expenses.list.viewPrepaid", { id: row.prepaid_id });
  return t("expenses.list.viewExpense", { id: row.id });
}

function formatDate(date?: string | null) {
  return formatAnyDateToLocale(date);
}

function formatAmount(amount: number) {
  return amount != null ? formatMoney(Number(amount || 0), "$") : "—";
}

function toBubbleType(
  statusKey: string,
): "paid" | "partial" | "unpaid" | "voided" {
  if (statusKey === "voided") return "voided";
  if (statusKey === "paid") return "paid";
  if (statusKey === "partial" || statusKey === "partially_paid")
    return "partial";
  if (statusKey === "unpaid" || statusKey === "open" || statusKey === "overdue")
    return "unpaid";
  return "unpaid";
}

function toBubbleLabel(statusKey: string) {
  if (statusKey === "paid") return "Paid";
  if (statusKey === "partial" || statusKey === "partially_paid")
    return "Partial";
  if (statusKey === "voided") return "Voided";
  return "Unpaid";
}

function getStatusKey(expense: ExpenseRow) {
  if (expense.voided) return "voided";
  if (expense.status) return expense.status.toLowerCase().trim();
  return "";
}

type StatusBubbleInfo = { label: string; bubbleType: "paid" | "partial" | "unpaid" | "voided" };

// A write-off's status bubble is NEVER Paid/Partial/Unpaid -- those are
// Expense-only concepts. It shows "Voided" only when the parent Deposit is
// voided (row.status === "Voided", set server-side -- see
// DepositWriteoffListRow), and shows no bubble at all otherwise (`null`),
// rather than falling through to Expense's "Unpaid" default.
function rowStatusBubble(row: OperationalRow): StatusBubbleInfo | null {
  if (isSyntheticRow(row)) {
    return row.voided ? { bubbleType: "voided", label: "Voided" } : null;
  }
  const statusKey = getStatusKey(row);
  return { bubbleType: toBubbleType(statusKey), label: toBubbleLabel(statusKey) };
}

function dateSortValue(value?: string | null) {
  if (!value) return null;
  const ms = new Date(value).getTime();
  return Number.isFinite(ms) ? ms : null;
}

// Type-aware tie-break: ExpenseRow.id (number), DepositWriteoffRow.id and
// PrepaidExpenseRow.id (namespaced strings) are drawn from independent
// sequences and are different JS types entirely -- they must never be
// compared or subtracted against each other directly. On a date tie, real
// Expenses sort before deposit write-offs, which sort before prepaid
// recognitions (matching the backend's own _operational_list_sort_key
// ranks 0/1/2), then each row breaks ties against same-type siblings only,
// using each type's own numeric id (ExpenseRow.id,
// DepositWriteoffRow.deposit_event_id, or
// PrepaidExpenseRow.prepaid_event_id).
function compareExpenseDates(a: OperationalRow, b: OperationalRow, dir: SortDir) {
  const da = dateSortValue(rowDate(a));
  const db = dateSortValue(rowDate(b));

  const ownId = (row: OperationalRow): number => {
    if (isWriteoffRow(row)) return row.deposit_event_id;
    if (isPrepaidExpenseRow(row)) return row.prepaid_event_id;
    return row.id;
  };
  const typeRank = (row: OperationalRow): number => {
    if (isWriteoffRow(row)) return 1;
    if (isPrepaidExpenseRow(row)) return 2;
    return 0;
  };

  const tieBreak = () => {
    const ra = typeRank(a);
    const rb = typeRank(b);
    if (ra !== rb) return dir === "asc" ? rb - ra : ra - rb;
    const ia = ownId(a);
    const ib = ownId(b);
    return dir === "asc" ? ia - ib : ib - ia;
  };

  if (da === null && db === null) return tieBreak();
  if (da === null) return 1;
  if (db === null) return -1;
  if (da !== db) return dir === "asc" ? da - db : db - da;

  return tieBreak();
}

function SourceDocumentIndicator() {
  const { t } = useTranslation();
  return (
    <span
      className="inline-flex h-6 w-6 items-center justify-center rounded-full text-gray-400"
      title={t("purchases.list.attached")}
      aria-label={t("purchases.list.attached")}
    >
      <PaperClipIcon className="h-4 w-4" aria-hidden="true" />
    </span>
  );
}

function downloadCsv(
  filename: string,
  headers: string[],
  rows: (string | number)[][],
) {
  const esc = (v: any) => {
    const s = String(v ?? "");
    return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
  };
  const csv =
    [headers.map(esc).join(","), ...rows.map((r) => r.map(esc).join(","))].join(
      "\n",
    ) + "\n";
  const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

const ROWS_PER_PAGE = 10;
type SortKey = "date" | "account";
type SortDir = "asc" | "desc";

export default function ExpenseList() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { company_id } = useParams<{ company_id: string }>();

  const [expenses, setExpenses] = useState<OperationalRow[]>([]);
  const [accounts, setAccounts] = useState<Account[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<ParseKeys | null>(null);

  // ✅ Toolbar state
  const [search, setSearch] = useState("");
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");

  const [page, setPage] = useState(1);

  // sorting state (kept as-is)
  const [sortKey, setSortKey] = useState<SortKey>("date");
  const [sortDir, setSortDir] = useState<SortDir>("desc");

  const returnTo = companyRoute(company_id, "/recognition/expenses");
  const newExpensePath =
    companyRoute(company_id, "/expenses/new") +
    `?return_to=${encodeURIComponent(returnTo)}`;

  useEffect(() => {
    if (!company_id) {
      setError("expenses.errors.noCompany");
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);

    const q = `?company_id=${company_id}&_=${Date.now()}`;

    Promise.all([
      apiGet<OperationalRow[]>(`/expenses${q}`),
      apiGet<Account[]>(`/accounts${q}`),
    ])
      .then(([expenses, accounts]) => {
        setExpenses(expenses);
        setAccounts(accounts);
      })
      .catch(() => setError("expenses.errors.loadList"))
      .finally(() => setLoading(false));
  }, [company_id]);

  // ✅ Reset page when filters/sort change
  useEffect(() => {
    setPage(1);
  }, [search, fromDate, toDate, sortKey, sortDir, expenses.length]);

  const filteredExpenses = useMemo(() => {
    const q = search.toLowerCase().trim();

    return expenses.filter((row) => {
      const ymd = (rowDate(row) || "").slice(0, 10);

      if (fromDate && ymd < fromDate) return false;
      if (toDate && ymd > toDate) return false;

      if (!q) return true;

      const vendor = getVendorName(row).toLowerCase();
      const category = (getCategoryName(row, accounts) || "").toLowerCase();
      // A synthetic row's searchable "status" text is only ever "voided" or
      // absent (see rowStatusBubble) -- never Expense's paid/partial/
      // unpaid vocabulary.
      const statusText = isSyntheticRow(row)
        ? (row.voided ? "voided" : "")
        : (row.status || "");

      return (
        vendor.includes(q) ||
        category.includes(q) ||
        statusText.toLowerCase().includes(q) ||
        (rowDate(row) || "").toLowerCase().includes(q) ||
        String(rowAmount(row)).includes(q)
      );
    });
  }, [expenses, search, fromDate, toDate, accounts]);

  const collator = useMemo(
    () => new Intl.Collator(undefined, { sensitivity: "base", numeric: true }),
    [],
  );

  const sortedExpenses = useMemo(() => {
    const list = filteredExpenses.slice();

    list.sort((a, b) => {
      if (sortKey === "date") {
        return compareExpenseDates(a, b, sortDir);
      } else {
        const na = getCategoryName(a, accounts);
        const nb = getCategoryName(b, accounts);
        const cmp = collator.compare(na, nb);
        if (cmp !== 0) return sortDir === "asc" ? cmp : -cmp;

        return compareExpenseDates(a, b, sortDir);
      }
    });

    return list;
  }, [filteredExpenses, sortKey, sortDir, accounts, collator]);

  const totalFiltered = sortedExpenses.length;
  const totalPages = Math.max(1, Math.ceil(totalFiltered / ROWS_PER_PAGE));
  const pagedExpenses = sortedExpenses.slice(
    (page - 1) * ROWS_PER_PAGE,
    page * ROWS_PER_PAGE,
  );

  const handleHeaderSort = (key: SortKey) => {
    if (sortKey === key) {
      setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    } else {
      setSortKey(key);
      setSortDir(key === "date" ? "desc" : "asc");
    }
  };

  // ✅ ListToolbar wiring
  const sortValue = `${sortKey}_${sortDir}`;
  const sortOptions: SortOption[] = [
    { label: t("sales.list.dateNewest"), value: "date_desc" },
    { label: t("sales.list.dateOldest"), value: "date_asc" },
    { label: t("expenses.list.categoryAsc"), value: "account_asc" },
    { label: t("expenses.list.categoryDesc"), value: "account_desc" },
  ];
  const onToolbarSortChange = (v: string) => {
    const [k, d] = v.split("_") as [SortKey, SortDir];
    setSortKey(k);
    setSortDir(d);
  };

  // CSV decision: write-off rows ARE included, with an explicit "Type"
  // column ("Expense" / "Deposit write-off"). The on-screen list shows
  // both record types by default with no toggle to hide write-offs, so a
  // CSV export that silently omitted them would show fewer/different rows
  // than what the user is currently looking at -- exactly the silent
  // mismatch this feature's own design (record_type as an explicit,
  // non-guessable discriminator) exists to avoid. Status text mirrors
  // rowStatusBubble: a write-off is never "Paid"/"Partial"/"Unpaid", only
  // "Voided" or blank.
  const exportCsv = () => {
    const headers = ["Type", "Date", "Category", "Vendor", "Amount", "Status", "ID"];
    const rows = sortedExpenses.map((row) => {
      const bubble = rowStatusBubble(row);
      return [
        isWriteoffRow(row)
          ? "Deposit write-off"
          : isPrepaidExpenseRow(row)
            ? "Prepaid recognition"
            : "Expense",
        formatDate(rowDate(row)),
        getCategoryName(row, accounts),
        getVendorName(row) || "",
        Number(rowAmount(row) || 0),
        bubble ? bubble.label : "",
        rowKey(row),
      ];
    });
    downloadCsv("expenses.csv", headers, rows);
  };

  const newItems: MenuItem[] = [
    {
      label: t("expenses.common.new"),
      onClick: () => navigate(newExpensePath),
    },
  ];

  const moreItems: MenuItem[] = [
    {
      label: t("directory.common.exportCsv"),
      onClick: exportCsv,
    },
  ];

  const pagerText = (
    <span className={`${A.label} text-gray-500 select-none`}>
      {t("directory.common.showing", {
        from: totalFiltered === 0 ? 0 : (page - 1) * ROWS_PER_PAGE + 1,
        to: totalFiltered === 0 ? 0 : Math.min(page * ROWS_PER_PAGE, totalFiltered),
        total: totalFiltered,
      })}
    </span>
  );

  if (loading)
    return (
      <div className={`${A.body} text-center py-6 text-gray-500`}>
        {t("expenses.list.loading")}
      </div>
    );
  if (error) return <EmptyState message={t(error)} heading={t("directory.common.nothingHere")} backLabel={t("directory.common.goBack")} />;

  if (!expenses.length) {
    return (
      <EmptyState
        message={t("expenses.list.empty")}
        heading={t("directory.common.nothingHere")}
        backLabel={t("directory.common.goBack")}
        showNewButton
        newButtonText={t("expenses.common.newPlus")}
        newButtonPath={newExpensePath}
      />
    );
  }

  return (
    <div className={`w-full ${A.body}`}>
      <ListToolbar
        moreLabel={t("directory.common.more")}
        sortLabel={t("directory.common.sort")}
        fromLabel={t("sales.shared.from")}
        toLabel={t("sales.shared.to")}
        openCalendarLabel={t("sales.shared.openCalendar")}
        maxWidthClass="max-w-6xl"
        search={search}
        onSearchChange={setSearch}
        searchPlaceholder={t("expenses.list.search")}
        sortValue={sortValue}
        sortOptions={sortOptions}
        onSortChange={onToolbarSortChange}
        fromDate={fromDate}
        toDate={toDate}
        onFromDateChange={setFromDate}
        onToDateChange={setToDate}
        newItems={newItems}
        moreItems={moreItems}
      />

      {/* Mobile Card View */}
      <div className="block md:hidden space-y-3 w-full max-w-6xl px-2">
        {pagedExpenses.map((row) => {
          const synthetic = isSyntheticRow(row);
          const bubble = rowStatusBubble(row);
          const vendorName = getVendorName(row);
          const rawCategoryName = getCategoryName(row, accounts);
          const categoryName = localizedAccountDisplayName(uniqueDisplayAccount(accounts, isSyntheticRow(row) ? row.expense_account_id : row.account_id, rawCategoryName), t, rawCategoryName);
          const key = rowKey(row);

          return (
            <div
              key={key}
              className={`${A.card} p-4 cursor-pointer transition hover:bg-gray-50 active:ring-2 active:ring-appleblue/20`}
              onClick={() => navigate(rowDetailPath(row, company_id))}
              tabIndex={0}
              aria-label={rowAriaLabel(row, t)}
              style={{
                minHeight: 112,
                display: "flex",
                flexDirection: "column",
                justifyContent: "space-between",
              }}
            >
              <div className="flex justify-between items-center mb-1">
                <span className="flex min-w-0 items-center gap-1.5">
                  <span className={`${A.body} font-medium text-gray-800`}>
                    {formatDate(rowDate(row))}
                  </span>
                  {!synthetic && row.source_document && (
                    <SourceDocumentIndicator />
                  )}
                </span>
                {bubble && (
                  <StatusBubble
                    bubbleType={bubble.bubbleType}
                    label={t(`expenses.status.${bubble.bubbleType}`)}
                  />
                )}
              </div>

              <div className="flex justify-between items-center gap-3">
                <div className="min-w-0">
                  {synthetic ? (
                    <StatusBubble
                      bubbleType="open"
                      label={t(row.record_type === "deposit_writeoff" ? "expenses.list.depositWriteoff" : "expenses.list.prepaidRecognition")}
                    />
                  ) : (
                    <div
                      className={`${A.body} font-medium text-gray-700 truncate`}
                    >
                      {categoryName}
                    </div>
                  )}

                  {vendorName && (
                    <div className={`${A.label} truncate mt-1`}>
                      {vendorName}
                    </div>
                  )}
                </div>

                <div className="text-right">
                  <div
                    className={`${A.body} font-medium text-gray-800 ${A.num}`}
                  >
                    {formatAmount(rowAmount(row))}
                  </div>
                </div>
              </div>

              {synthetic ? (
                <div className="mt-2 text-xs text-gray-400 truncate">
                  {categoryName}
                  {row.memo ? ` · ${row.memo}` : ""}
                </div>
              ) : (
                <div className="mt-2 h-5" />
              )}
            </div>
          );
        })}
      </div>

      {/* Desktop Table */}
      <ListTableShell maxWidthClass="max-w-6xl">
        <table className={`w-full ${A.body}`}>
          <thead>
            <tr className="border-b border-gray-100">
              <th
                className={`py-3 px-4 text-left ${A.label} select-none cursor-pointer`}
                onClick={() => handleHeaderSort("date")}
                title={t("sales.list.sortDate")}
              >
                {t("sales.common.date")}{" "}
                {sortKey === "date" && (
                  <span className="text-gray-400">
                    {sortDir === "asc" ? "↑" : "↓"}
                  </span>
                )}
              </th>
              <th
                className={`py-3 px-2 text-left ${A.label} select-none cursor-pointer`}
                onClick={() => handleHeaderSort("account")}
                title={t("expenses.list.sortCategory")}
              >
                {t("expenses.common.category")}{" "}
                {sortKey === "account" && (
                  <span className="text-gray-400">
                    {sortDir === "asc" ? "↑" : "↓"}
                  </span>
                )}
              </th>
              <th className={`py-3 px-2 text-left ${A.label}`}>{t("expenses.common.detail")}</th>
              <th className={`py-3 px-2 text-right ${A.label}`}>
                {t("sales.common.amount")}
              </th>
              <th className={`py-3 px-2 text-center ${A.label}`}>
                {t("sales.common.status")}
              </th>
            </tr>
          </thead>

          <tbody>
            {pagedExpenses.map((row) => {
              const synthetic = isSyntheticRow(row);
              const bubble = rowStatusBubble(row);
              const vendorName = getVendorName(row);
              const rawCategoryName = getCategoryName(row, accounts);
          const categoryName = localizedAccountDisplayName(uniqueDisplayAccount(accounts, isSyntheticRow(row) ? row.expense_account_id : row.account_id, rawCategoryName), t, rawCategoryName);
              const key = rowKey(row);
              const goToDetail = () => navigate(rowDetailPath(row, company_id));

              return (
                <tr
                  key={key}
                  className="border-b border-gray-50 bg-white hover:bg-gray-50 cursor-pointer text-gray-700 transition"
                  onClick={goToDetail}
                  tabIndex={0}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      goToDetail();
                    }
                  }}
                  style={{ outline: "none" }}
                  aria-label={rowAriaLabel(row, t)}
                >
                  <td className="py-3 px-4">
                    <span className="inline-flex items-center gap-1.5">
                      <span>{formatDate(rowDate(row))}</span>
                      {!synthetic && row.source_document && (
                        <SourceDocumentIndicator />
                      )}
                    </span>
                  </td>
                  <td className="py-3 px-2">
                    {synthetic ? (
                      <StatusBubble
                        bubbleType="open"
                        label={t(row.record_type === "deposit_writeoff" ? "expenses.list.depositWriteoff" : "expenses.list.prepaidRecognition")}
                      />
                    ) : (
                      categoryName
                    )}
                  </td>
                  <td className="py-3 px-2">
                    {synthetic ? (
                      <div className="min-w-0">
                        <div className="truncate">
                          {vendorName || <span className="text-gray-400">—</span>}
                        </div>
                        <div className="text-xs text-gray-400 truncate">
                          {categoryName}
                          {row.memo ? ` · ${row.memo}` : ""}
                        </div>
                      </div>
                    ) : (
                      vendorName || <span className="text-gray-400">—</span>
                    )}
                  </td>
                  <td className={`py-3 px-2 text-right font-medium ${A.num}`}>
                    {formatAmount(rowAmount(row))}
                  </td>
                  <td className="py-3 px-2 text-center">
                    {bubble && (
                      <StatusBubble
                        bubbleType={bubble.bubbleType}
                        label={t(`expenses.status.${bubble.bubbleType}`)}
                      />
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>

        {/* Pagination */}
        <div className="flex justify-between items-center mt-2 px-3 pb-3">
          {pagerText}
          <div className="flex gap-2">
            <Button
              variant="tertiary"
              minWidth="0px"
              className="px-2 active:scale-100"
              disabled={page === 1}
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              type="button"
            >
              {t("directory.common.prev")}
            </Button>

            <Button
              variant="tertiary"
              minWidth="0px"
              className="px-2 active:scale-100"
              disabled={page === totalPages || totalPages === 0}
              onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
              type="button"
            >
              {t("directory.common.next")}
            </Button>
          </div>
        </div>
      </ListTableShell>

      {/* Pagination for mobile */}
      <div className="md:hidden flex justify-between items-center mt-4 w-full max-w-6xl px-2">
        {pagerText}
        <div className="flex gap-2">
          <Button
            variant="tertiary"
            minWidth="0px"
            className="px-2 active:scale-100"
            disabled={page === 1}
            onClick={() => setPage((p) => Math.max(1, p - 1))}
            type="button"
          >
            {t("directory.common.prev")}
          </Button>

          <Button
            variant="tertiary"
            minWidth="0px"
            className="px-2 active:scale-100"
            disabled={page === totalPages || totalPages === 0}
            onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
            type="button"
          >
            {t("directory.common.next")}
          </Button>
        </div>
      </div>
    </div>
  );
}
